import React from "react"
import IconButton from "@material-ui/core/IconButton"
import Menu from "@material-ui/core/Menu"
import MenuItem from "@material-ui/core/MenuItem"
import ListItemIcon from "@material-ui/core/ListItemIcon"
import ListItemText from "@material-ui/core/ListItemText"
import { ArrowLeft, ArrowRight, ExternalLink, MoreHorizontal } from "react-feather"
import { Board } from "../../entities/board"
import { useOnMoveCard } from "../../hooks/use-on-move-card"
import { makeStyles, Theme } from "@material-ui/core"
import { Link } from "react-router-dom"
import { useUrlWithContext } from "../../../hooks/use-url-with-context"
import { paths } from "../../../paths"
import { useTranslations } from "../../hooks/use-translations"
import { BoardViewModelV2 } from "../../api/v2/board-view-model"
import MoveToColumnWidget from "../MoveToColumnWidget"

type CardViewModel = BoardViewModelV2["columnsById"][string]["cards"][number]
type BoardCardProps = {
  board: BoardViewModelV2
  card: CardViewModel
}

const BoardCardMenu = ({ board, card }: BoardCardProps) => {
  const classes = useStyles()
  const translations = useTranslations()
  const [menuAncohorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null)
  const { onMoveCard } = useOnMoveCard()
  const { createPathWithContext } = useUrlWithContext()

  const boardInstance = new Board(board)
  const visibleColumns = boardInstance.getVisibleColumns()
  const currentColumnIndex = boardInstance.getCardById(card.id)?.column as number
  const currentColumnId = boardInstance.getColumnByPosition(currentColumnIndex)?.id

  const nextVisibleColumn = boardInstance.getNextVisibleColumn(currentColumnIndex)
  const prevVisibleColumn = boardInstance.getPrevVisibleColumn(currentColumnIndex)
  const isMoveToLeftDisabled = prevVisibleColumn === null
  const isMoveToRightDisabled = nextVisibleColumn === null
  const taskKanbanPath = createPathWithContext(paths.taskKanban(card.id))

  const onMoveLeft = () => {
    if (!currentColumnId || !prevVisibleColumn) return

    onMoveCard({
      board: board,
      position: prevVisibleColumn.getNextCardPosition(),
      boardId: board.id,
      cardId: card.id,
      columnId: currentColumnId,
      destColumnId: prevVisibleColumn.id,
    })
  }

  const onMoveRight = () => {
    if (!currentColumnId || !nextVisibleColumn) return

    onMoveCard({
      board: board,
      position: nextVisibleColumn.getNextCardPosition(),
      boardId: board.id,
      cardId: card.id,
      columnId: currentColumnId,
      destColumnId: nextVisibleColumn.id,
    })
  }

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <IconButton onClick={(e) => setMenuAnchorEl(e.currentTarget)} style={{ marginRight: -8, padding: 4 }}>
        <MoreHorizontal size={16} />
      </IconButton>
      <Menu
        anchorEl={menuAncohorEl}
        open={Boolean(menuAncohorEl)}
        onClose={() => setMenuAnchorEl(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {card.type === "task" ? (
          <MenuItem divider button className={classes.openKanbanButton} component={Link} to={taskKanbanPath} dense>
            <ListItemIcon>
              <ExternalLink size={16} />
            </ListItemIcon>
            <ListItemText primary={translations.openKanbanBtnLabel} />
          </MenuItem>
        ) : null}
        <MenuItem
          disabled={isMoveToRightDisabled}
          onClick={() => {
            onMoveRight()
            setMenuAnchorEl(null)
          }}
          dense
        >
          <ListItemIcon>
            <ArrowRight className={classes.arrowIcon} />
          </ListItemIcon>
          <ListItemText
            primary={translations.moveToRight}
            secondary={nextVisibleColumn ? `${translations.cardWillMoveTo} ${nextVisibleColumn.getName()}` : undefined}
          />
        </MenuItem>
        <MenuItem
          disabled={isMoveToLeftDisabled}
          onClick={() => {
            onMoveLeft()
            setMenuAnchorEl(null)
          }}
          dense
        >
          <ListItemIcon>
            <ArrowLeft className={classes.arrowIcon} />
          </ListItemIcon>
          <ListItemText
            primary={translations.moveToLeft}
            secondary={prevVisibleColumn ? `${translations.cardWillMoveTo} ${prevVisibleColumn.getName()}` : undefined}
          />
        </MenuItem>
        {visibleColumns.length > 1 && currentColumnId && (
          <MenuItem button={false} dense>
            <ListItemIcon>{null}</ListItemIcon>
            <ListItemText
              primary={translations.moveToColumn}
              secondaryTypographyProps={{
                component: "div",
              }}
              secondary={<MoveToColumnWidget board={board} cardId={card.id} hideTitle />}
            />
          </MenuItem>
        )}
      </Menu>
    </div>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  openKanbanButton: {
    color: theme.palette.primary.main,
    "& .MuiListItemIcon-root": {
      color: theme.palette.primary.main,
    },
  },
  arrowIcon: {
    color: theme.palette.common.black,
    width: 20,
    height: 20,
  },
  select: {
    marginTop: theme.spacing(1),
    fontSize: "0.875rem",
    "& .MuiSelect-root": {
      paddingTop: 8,
      paddingBottom: 8,
    },
  },
}))

export default BoardCardMenu
